function formatDroneFacilityPopup(feature) {
  const attrs = feature?.properties || feature?.attributes || {};
  const ceiling = Number(attrs.CEILING);
  const ceilingText = Number.isFinite(ceiling) ? `${ceiling} ${attrs.UNIT || "ft"}` : "Unknown";
  const airports = [1, 2, 3, 4, 5]
    .map((idx) => ({
      name: String(attrs[`APT${idx}_NAME`] || "").trim(),
      code: String(attrs[`APT${idx}_ICAO`] || attrs[`APT${idx}_FAAid`] || attrs[`APT${idx}_FAAID`] || "").trim(),
      enabled: String(attrs[`APT${idx}_Enabled`] || "").trim(),
      laanc: attrs[`APT${idx}_LAANC`],
    }))
    .filter((row) => row.name || row.code);
  const airportLines = airports.length
    ? airports.map((row) => {
        const parts = [row.name || row.code];
        if (row.code && row.name && row.code !== row.name) parts.push(`(${row.code})`);
        if (row.enabled) parts.push(row.enabled);
        else if (Number(row.laanc) === 1) parts.push("LAANC");
        return `<li>${parts.join(" - ")}</li>`;
      }).join("")
    : "<li>No associated airport listed</li>";
  const airspace = [1, 2, 3, 4, 5]
    .map((idx) => String(attrs[`AIRSPACE_${idx}`] || "").trim())
    .filter(Boolean)
    .join(", ");
  return `
    <div style="min-width:220px;">
      <div style="font-weight:700; margin-bottom:4px;">FAA UAS Facility Map</div>
      <div><strong>Grid ceiling:</strong> ${ceilingText}</div>
      ${attrs.APT1_NAME ? `<div><strong>Primary airport:</strong> ${attrs.APT1_NAME}</div>` : ""}
      ${airspace ? `<div><strong>Airspace:</strong> ${airspace}</div>` : ""}
      ${attrs.MAP_EFF ? `<div><strong>Map effective:</strong> ${attrs.MAP_EFF}</div>` : ""}
      ${attrs.LAST_EDIT ? `<div><strong>Last edit:</strong> ${attrs.LAST_EDIT}</div>` : ""}
      <div style="margin-top:6px;"><strong>Associated airports</strong></div>
      <ul style="margin:4px 0 0 16px; padding:0;">${airportLines}</ul>
    </div>
  `;
}

function droneFacilityStyle(feature) {
  const attrs = feature?.properties || feature?.attributes || {};
  const ceiling = Number(attrs.CEILING);
  if (!Number.isFinite(ceiling) || ceiling <= 0) {
    return { color: "#7f1d1d", fillColor: "#dc2626", fillOpacity: 0.42, weight: 1.2 };
  }
  if (ceiling <= 100) {
    return { color: "#9a3412", fillColor: "#f97316", fillOpacity: 0.32, weight: 1.1 };
  }
  if (ceiling <= 200) {
    return { color: "#a16207", fillColor: "#facc15", fillOpacity: 0.24, weight: 1.05 };
  }
  if (ceiling <= 300) {
    return { color: "#3f6212", fillColor: "#a3e635", fillOpacity: 0.2, weight: 1 };
  }
  return { color: "#166534", fillColor: "#22c55e", fillOpacity: 0.18, weight: 1 };
}

function esc(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function formatPlannedSurveyPopup(feature) {
  const a = feature?.properties || feature?.attributes || {};
  return `
    <div style="min-width:200px;">
      <div style="font-weight:700; margin-bottom:4px;">OCS Survey Plan</div>
      ${a.projectname ? `<div><strong>Project:</strong> ${esc(a.projectname)}</div>` : ""}
      ${a.project_number ? `<div><strong>Number:</strong> ${esc(a.project_number)}</div>` : ""}
      ${a.registry_number ? `<div><strong>Registry:</strong> ${esc(a.registry_number)}</div>` : ""}
      ${a.cy ? `<div><strong>Calendar year:</strong> ${esc(a.cy)}</div>` : ""}
      ${a.platform ? `<div><strong>Platform:</strong> ${esc(a.platform)}</div>` : ""}
      ${a.snm ? `<div><strong>Area:</strong> ${esc(a.snm)} sq nm</div>` : ""}
      ${a.locality ? `<div><strong>Locality:</strong> ${esc(a.locality)}</div>` : ""}
      ${a.sub_locality ? `<div><strong>Sub-locality:</strong> ${esc(a.sub_locality)}</div>` : ""}
    </div>
  `;
}

const SURVEY_FS_URL = "https://gis.charttools.noaa.gov/arcgis/rest/services/Hydrographic_Services/Planned_Survey_Areas/FeatureServer/0";

const SURVEY_YEAR_COLORS = {
  2020: { fill: "#94a3b8", stroke: "#475569" },
  2021: { fill: "#7dd3fc", stroke: "#0284c7" },
  2022: { fill: "#6ee7b7", stroke: "#059669" },
  2023: { fill: "#fde68a", stroke: "#d97706" },
  2024: { fill: "#fdba74", stroke: "#ea580c" },
  2025: { fill: "#c4b5fd", stroke: "#7c3aed" },
  2026: { fill: "#60a5fa", stroke: "#1d4ed8" },
  2027: { fill: "#f9a8d4", stroke: "#be185d" },
  2028: { fill: "#86efac", stroke: "#15803d" },
  2029: { fill: "#fca5a5", stroke: "#dc2626" },
};

function plannedSurveyStyle(feature) {
  const a = feature?.properties || feature?.attributes || {};
  const year = Number(a.cy);
  const c = (Number.isFinite(year) && SURVEY_YEAR_COLORS[year]) || { fill: "#94a3b8", stroke: "#64748b" };
  return { color: c.stroke, fillColor: c.fill, fillOpacity: 0.28, weight: 1.2 };
}

function plannedSurveyTooltip(feature) {
  const a = feature?.properties || feature?.attributes || {};
  const parts = [a.projectname, a.cy].filter(Boolean);
  return parts.join(" · ") || "OCS Survey Plan";
}

function buildUtmZonesGeoJson() {
  const features = [];
  for (let zone = 1; zone <= 60; zone++) {
    const west = -180 + (zone - 1) * 6;
    const east = west + 6;
    features.push({
      type: "Feature",
      properties: { zone, name: `UTM Zone ${zone}`, west, east },
      geometry: {
        type: "Polygon",
        coordinates: [[[west, -80], [east, -80], [east, 84], [west, 84], [west, -80]]],
      },
    });
  }
  return { type: "FeatureCollection", features };
}

function buildUtmZonesLayer() {
  return L.geoJSON(buildUtmZonesGeoJson(), {
    style: () => ({
      color: "#38bdf8",
      weight: 1,
      opacity: 0.75,
      fillColor: "#38bdf8",
      fillOpacity: 0.04,
      dashArray: "5 3",
    }),
    onEachFeature(feature, layer) {
      const { zone, west, east } = feature.properties;
      layer.bindTooltip(String(zone), { permanent: true, direction: "center", className: "zone-label-tip" });
      layer.bindPopup(
        `<div style="min-width:130px;">
          <div style="font-weight:700;margin-bottom:4px;">UTM Zone ${zone}</div>
          <div><strong>Longitude:</strong> ${west}&deg; to ${east}&deg;</div>
          <div><strong>EPSG (North):</strong> 326${String(zone).padStart(2, "0")}</div>
          <div><strong>EPSG (South):</strong> 327${String(zone).padStart(2, "0")}</div>
        </div>`
      );
    },
  });
}

function statePlaneZoneStyle() {
  return { color: "#f59e0b", weight: 1, opacity: 0.75, fillColor: "#fbbf24", fillOpacity: 0.05 };
}

const SPCS_SUFFIX = {
  E: "East", W: "West", N: "North", S: "South", C: "Central",
  NC: "N Central", SC: "S Central", EC: "E Central", WC: "W Central",
  LI: "Long Is", M: "Mainland", OS: "Offshore",
};
const SPCS_OVERRIDES = {
  CA_I: "CA I", CA_II: "CA II", CA_III: "CA III",
  CA_IV: "CA IV", CA_V: "CA V", CA_VI: "CA VI",
  MA_M: "MA Mainland", MA_I: "MA Island",
  NY_LI: "NY Long Is", LA_OS: "LA Offshore",
  TX_NC: "TX N Central", TX_SC: "TX S Central",
  WY_EC: "WY E Central", WY_WC: "WY W Central",
};

function friendlySpcsLabel(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (SPCS_OVERRIDES[s]) return SPCS_OVERRIDES[s];
  const sep = s.indexOf("_");
  if (sep < 0) return s;
  const state = s.slice(0, sep);
  const suffix = s.slice(sep + 1);
  const word = SPCS_SUFFIX[suffix];
  if (word) return `${state} ${word}`;
  return `${state} ${suffix}`;
}

function statePlaneZoneLabel(feature) {
  const a = feature?.properties || feature?.attributes || {};
  const raw = String(a.ZONE || a.FIPS_ZONE || a.ZONENAME || a.NAME || "").trim();
  return friendlySpcsLabel(raw);
}

function formatStatePlanePopup(feature) {
  const a = feature?.properties || feature?.attributes || {};
  const rawZone = String(a.ZONE || a.FIPS_ZONE || a.ZONENAME || a.NAME || "").trim();
  const zone = friendlySpcsLabel(rawZone);
  const state = a.STATE || a.STATE_NAME || a.STATEPLANE || "";
  const units = a.UNITS || a.UNIT || "";
  const fips = a.FIPS_ZONE || a.FIPS || "";
  return `
    <div style="min-width:160px;">
      <div style="font-weight:700;margin-bottom:4px;">State Plane Zone</div>
      ${zone ? `<div><strong>Zone:</strong> ${esc(zone)}</div>` : ""}
      ${state ? `<div><strong>State:</strong> ${esc(state)}</div>` : ""}
      ${fips ? `<div><strong>FIPS:</strong> ${esc(fips)}</div>` : ""}
      ${units ? `<div><strong>Units:</strong> ${esc(units)}</div>` : ""}
    </div>
  `;
}

const CATALOG = [
  {
    id: "osm",
    name: "OpenStreetMap",
    kind: "basemap",
    type: "xyz",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    options: { maxNativeZoom: 19, maxZoom: 24, attribution: "&copy; OpenStreetMap" },
    opacity: 1,
  },
  {
    id: "esri-imagery",
    name: "Esri World Imagery",
    kind: "basemap",
    type: "xyz",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    options: { maxNativeZoom: 19, maxZoom: 24, attribution: "Tiles &copy; Esri" },
    opacity: 1,
  },
  {
    id: "pmtiles-offline",
    name: "PMTiles (Offline)",
    kind: "basemap",
    type: "pmtiles-raster",
    url: "/pmtiles/offline.pmtiles",
    options: {
      maxNativeZoom: 14,
      maxZoom: 24,
      attribution: "Offline PMTiles",
    },
    opacity: 1,
  },
  {
    id: "faa-uas-facility-map",
    name: "Drone Airspace (FAA UAS Facility Map)",
    kind: "overlay",
    type: "esri-feature",
    url: "https://services6.arcgis.com/ssFJjBXIUyZDrSYZ/arcgis/rest/services/FAA_UAS_FacilityMap_Data_V5/FeatureServer/0",
    options: {
      simplifyFactor: 0.35,
      precision: 5,
      styleBuilder: droneFacilityStyle,
      popupBuilder: formatDroneFacilityPopup,
      attribution: "FAA UAS Facility Maps",
    },
    opacity: 0.8,
  },
  {
    id: "utm-zones",
    name: "UTM Zones (World)",
    kind: "overlay",
    group: "Reference Grids",
    type: "leaflet-group",
    buildLeaflet: buildUtmZonesLayer,
    opacity: 0.8,
    volatile: true,
  },
  {
    id: "state-plane-zones",
    name: "State Plane Coordinate Zones (US)",
    kind: "overlay",
    group: "Reference Grids",
    type: "esri-feature",
    url: "https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/USA_State_Plane_Zones_NAD83/FeatureServer/0",
    options: {
      simplifyFactor: 0.5,
      precision: 4,
      styleBuilder: statePlaneZoneStyle,
      popupBuilder: formatStatePlanePopup,
      tooltipBuilder: statePlaneZoneLabel,
      attribution: "Esri Living Atlas / NGS",
    },
    opacity: 0.8,
  },
  {
    id: "radar-nexrad-conus",
    name: "Radar (NEXRAD CONUS, live)",
    kind: "overlay",
    type: "xyz",
    url: "https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q/{z}/{x}/{y}.png",
    options: {
      maxNativeZoom: 12,
      maxZoom: 24,
      attribution: "IEM / NEXRAD",
    },
    opacity: 0.72,
  },
  {
    id: "satellite-goes-east-conus",
    name: "Satellite (GOES-East CONUS Channel 2)",
    kind: "overlay",
    type: "xyz",
    url: "https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/goes_east_conus_ch02/{z}/{x}/{y}.png",
    options: {
      maxNativeZoom: 10,
      maxZoom: 24,
      attribution: "IEM / NOAA GOES",
    },
    opacity: 0.65,
  },
  {
    id: "fema-flood-hazard",
    name: "FEMA Flood Hazard (NFHL)",
    kind: "overlay",
    type: "wms",
    url: "https://hazards.fema.gov/arcgis/services/public/NFHL/MapServer/WMSServer",
    options: {
      layers: "0",
      format: "image/png",
      transparent: true,
      version: "1.3.0",
      opacity: 0.8,
      maxZoom: 24,
      attribution: "FEMA NFHL",
    },
    opacity: 0.8,
  },
  {
    id: "etopo-hillshade",
    name: "ETOPO (NCEI hillshade)",
    kind: "overlay",
    type: "wms",
    url: "https://gis.ngdc.noaa.gov/arcgis/services/DEM_mosaics/DEM_global_mosaic_hillshade/ImageServer/WMSServer",
    options: {
      layers: "DEM_global_mosaic_hillshade:ColorHillshade",
      format: "image/png",
      transparent: true,
      version: "1.1.1",
      crsCode: "EPSG:4326",
      opacity: 0.8,
      maxZoom: 24,
      attribution: "NOAA NCEI",
    },
    opacity: 0.8,
  },
  {
    id: "ncei-dem-global",
    name: "NCEI DEM Global Mosaic (raw elevation)",
    kind: "overlay",
    type: "esri-image",
    url: "https://gis.ngdc.noaa.gov/arcgis/rest/services/DEM_mosaics/DEM_global_mosaic/ImageServer",
    options: { opacity: 0.75 },
    opacity: 0.75,
  },
  {
    id: "ncei-multibeam-footprints",
    name: "NCEI Multibeam Survey Footprints",
    kind: "overlay",
    group: "NCEI Bathymetric Viewer",
    type: "esri-feature",
    url: "https://gis.ngdc.noaa.gov/arcgis/rest/services/web_mercator/multibeam_dynamic/FeatureServer/0",
    options: { attribution: "NOAA NCEI" },
    opacity: 0.75,
  },
  {
    id: "ncei-multibeam-mosaic",
    name: "NCEI Multibeam Bathymetry Mosaic",
    kind: "overlay",
    group: "NCEI Bathymetric Viewer",
    type: "esri-image",
    url: "https://gis.ngdc.noaa.gov/arcgis/rest/services/multibeam_mosaics/multibeam_mosaic_combined/ImageServer",
    options: { opacity: 0.8 },
    opacity: 0.8,
  },
  {
    id: "ncei-bag-shaded-relief",
    name: "NCEI BAG Color Shaded Relief",
    kind: "overlay",
    group: "NCEI Bathymetric Viewer",
    type: "esri-image",
    url: "https://gis.ngdc.noaa.gov/arcgis/rest/services/NOS_MBAB/NOS_MBAB_U8/ImageServer",
    options: { opacity: 0.8 },
    opacity: 0.8,
  },
  {
    id: "ncei-nos-hydro",
    name: "NCEI NOS Hydrographic Surveys",
    kind: "overlay",
    group: "NCEI Bathymetric Viewer",
    type: "wms",
    url: "https://gis.ngdc.noaa.gov/arcgis/services/web_mercator/nos_hydro_dynamic/MapServer/WMSServer",
    options: {
      layers: "0,1,2",
      format: "image/png",
      transparent: true,
      version: "1.1.1",
      opacity: 0.75,
      maxZoom: 24,
      attribution: "NOAA NCEI",
    },
    opacity: 0.75,
  },
  {
    id: "gebco",
    name: "GEBCO Bathymetry (WMS)",
    kind: "overlay",
    type: "wms",
    url: "https://wms.gebco.net/mapserv?",
    options: {
      layers: "GEBCO_LATEST",
      format: "image/png",
      transparent: true,
      opacity: 0.7,
      maxZoom: 22,
    },
    opacity: 0.7,
  },
  {
    id: "bluetopo",
    name: "NOAA BlueTopo (elevation)",
    kind: "overlay",
    type: "wms",
    url: "https://nowcoast.noaa.gov/geoserver/bluetopo/wms",
    options: {
      layers: "bathymetry",
      styles: "nbs_elevation",
      format: "image/png",
      transparent: true,
      version: "1.1.1",
      opacity: 0.7,
      maxZoom: 22,
    },
    opacity: 0.7,
  },
  {
    id: "noaa-chart-display-tiles",
    name: "NOAA Chart Display (WMTS Tiles)",
    kind: "overlay",
    type: "xyz",
    url: "https://gis.charttools.noaa.gov/arcgis/rest/services/MarineChart_Services/NOAACharts/MapServer/WMTS/tile/1.0.0/MarineChart_Services_NOAACharts/default/GoogleMapsCompatible/{z}/{y}/{x}.png",
    options: {
      maxNativeZoom: 7,
      maxZoom: 24,
      attribution: "NOAA Office of Coast Survey",
    },
    opacity: 0.95,
  },
  {
    id: "noaa-chart-display-traditional-wms",
    name: "NOAA Chart Display (Traditional, WMS)",
    kind: "overlay",
    type: "wms",
    url: "https://gis.charttools.noaa.gov/arcgis/rest/services/MCS/NOAAChartDisplay/MapServer/exts/MaritimeChartService/WMSServer",
    options: {
      // Keep only core chart content; omit noisy pattern/quality/overscale layers.
      layers: "1,2,3,4,5,6,7",
      format: "image/png",
      transparent: true,
      version: "1.1.1",
      opacity: 0.95,
      maxZoom: 24,
      attribution: "NOAA Office of Coast Survey",
    },
    opacity: 0.95,
  },
  {
    id: "noaa-ecdis-display",
    name: "NOAA ECDIS Display (S-52)",
    kind: "overlay",
    type: "wms",
    url: "https://gis.charttools.noaa.gov/arcgis/rest/services/MCS/ENCOnline/MapServer/exts/MaritimeChartService/WMSServer",
    options: {
      // Keep only core chart content; omit noisy pattern/quality/overscale layers.
      layers: "1,2,3,4,5,6,7",
      format: "image/png",
      transparent: true,
      version: "1.1.1",
      opacity: 0.95,
      maxZoom: 24,
      attribution: "NOAA Office of Coast Survey",
    },
    opacity: 0.95,
  },
  {
    id: "noaa-planned-survey-areas",
    name: "NOAA Planned Survey Areas (OCS)",
    kind: "overlay",
    group: "NOAA Survey",
    type: "esri-feature",
    url: "https://gis.charttools.noaa.gov/arcgis/rest/services/Hydrographic_Services/Planned_Survey_Areas/FeatureServer/0",
    options: {
      simplifyFactor: 0.5,
      precision: 5,
      styleBuilder: plannedSurveyStyle,
      popupBuilder: formatPlannedSurveyPopup,
      tooltipBuilder: plannedSurveyTooltip,
      tooltipOptions: { permanent: false, sticky: true, className: "zone-label-tip" },
      attribution: "NOAA Office of Coast Survey",
    },
    opacity: 0.8,
  },
  {
    id: "historic-vessel-activity-global-gmtds",
    name: "Historic Vessel Activity (Global, GMTDS)",
    kind: "overlay",
    type: "wms",
    url: "https://gmtds.maplarge.com/ogc/ais:density/wms",
    options: {
      layers: "ais:density",
      format: "image/png",
      transparent: true,
      version: "1.3.0",
      styles: "",
      maxZoom: 24,
      opacity: 0.65,
      attribution: "NGA GMTDS / MapLarge",
    },
    opacity: 0.65,
  },
];

export const overlayManagerTool = {
  id: "overlay-manager",
  name: "Overlay Manager",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  serialize(app, options) { return this._rt?.snapshot?.(options) ?? null; },
  restore(app, data, options) { if (!this._rt) this._rt = createRuntime(app); return this._rt.restore?.(data, options); },
  resolveReference(app, referenceId, file) { if (!this._rt) this._rt = createRuntime(app); return this._rt.resolveReference?.(referenceId, file); },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};

function createRuntime(app) {
  const RASTER_MONITOR_KEY = "hydrotools:raster-job-monitor:v1";
  const NOAA_CLEAN_WMS_LAYERS = "1,2,3,4,5,6,7";
  const ETOPO_HILLSHADE_WMS_URL = "https://gis.ngdc.noaa.gov/arcgis/services/DEM_mosaics/DEM_global_mosaic_hillshade/ImageServer/WMSServer";
  const ETOPO_HILLSHADE_WMS_LAYERS = "DEM_global_mosaic_hillshade:ColorHillshade";
  let containerEl = null;
  const unresolvedRefs = new Map();
  let migratedNoaaWmsOnce = false;
  let lastState = {
    basemapId: "",
    overlayId: "",
    geotiffUrl: "",
  };
  let importProgress = {
    active: false,
    complete: false,
    failed: false,
    progressPct: 0,
    stage: "",
    message: "",
    startedAt: 0,
    finishedAt: 0,
  };
  let activeRasterJobId = "";
  let monitoredRasterJobId = "";
  let rasterJobPollTimer = null;
  let monitoredRasterJob = null;
  const rasterJobLayerMap = new Map();

  let surveyLayerId = "";
  let surveyYears = new Set();
  let surveyPlatforms = new Set();
  let surveyYearsEmpty = false;
  let surveyPlatsEmpty = false;
  let surveyMeta = { years: [], platforms: [], loading: false, loaded: false };
  let surveyTableData = { rows: [], totalSnm: 0, loading: false, loaded: false, selectedIds: new Set() };
  let surveyOpenDropdown = "";
  let layersChangedOff = null;

  const basemapOptions = CATALOG.filter((l) => l.kind === "basemap")
    .map((l) => `<option value="${l.id}">${l.name}</option>`)
    .join("");
  const overlayOptions = CATALOG.filter((l) => l.kind !== "basemap")
    .map((l) => `<option value="${l.id}">${l.name}</option>`)
    .join("");

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error || new Error("file read failed"));
      reader.readAsDataURL(file);
    });
  }

  function dataUrlToObjectUrl(dataUrl) {
    if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) return null;
    const comma = dataUrl.indexOf(",");
    if (comma < 0) return null;
    const head = dataUrl.slice(0, comma);
    const body = dataUrl.slice(comma + 1);
    const mimeMatch = /^data:([^;]+)/i.exec(head);
    const mime = mimeMatch?.[1] || "application/octet-stream";
    const isBase64 = /;base64/i.test(head);
    const bytes = isBase64 ? Uint8Array.from(atob(body), (c) => c.charCodeAt(0)) : new TextEncoder().encode(decodeURIComponent(body));
    const blob = new Blob([bytes], { type: mime });
    return URL.createObjectURL(blob);
  }

  function collectOverlayLayersForProject(dataPolicy = "reference") {
    const layers = app.services.layers.listLayers().filter((l) => l.toolId === "overlay-manager");
    return layers.map((l) => {
      const meta = { ...(l.meta || {}) };
      const out = {
        id: l.id,
        name: l.name,
        group: l.group ?? null,
        kind: l.kind,
        type: l.type,
        url: l.url,
        options: l.options ?? {},
        opacity: Number.isFinite(Number(l.opacity)) ? Number(l.opacity) : 1,
        visible: l.visible !== false,
        meta,
      };
      if (l.type === "geotiff-url" && String(l.url || "").startsWith("blob:")) {
        if (dataPolicy === "embed" && typeof meta.embeddedDataUrl === "string" && meta.embeddedDataUrl.startsWith("data:")) {
          out.url = null;
          out.embeddedDataUrl = meta.embeddedDataUrl;
          out.meta = { ...meta, sourceType: "local-file-embedded" };
        } else {
          out.url = null;
          out.meta = { ...meta, unresolved: true, sourceType: "local-file-reference" };
        }
      }
      return out;
    });
  }

  function pullFormState() {
    if (!containerEl) return;
    lastState = {
      basemapId: String(containerEl.querySelector("#lm-basemap-select")?.value || ""),
      overlayId: String(containerEl.querySelector("#lm-overlay-select")?.value || ""),
      geotiffUrl: String(containerEl.querySelector("#lm-geotiff-url")?.value || ""),
    };
  }

  function render() {
    const basemapOptions = CATALOG.filter((l) => l.kind === "basemap")
      .map((l) => `<option value="${l.id}">${l.name}</option>`)
      .join("");
    const overlayOptions = CATALOG.filter((l) => l.kind !== "basemap")
      .map((l) => `<option value="${l.id}">${l.name}</option>`)
      .join("");
    if (!containerEl) return;
    const progressWidth = Math.max(0, Math.min(100, Number(importProgress.progressPct) || 0));
    const progressLabel = importProgress.failed
      ? "Import failed"
      : importProgress.complete
        ? "Import complete"
        : importProgress.active
          ? "Importing..."
          : "Idle";
    containerEl.innerHTML = `
        <div class="lm-add-card">
          <div class="lm-title">Add Basemap</div>
          <div class="row lm-add-row">
            <select id="lm-basemap-select" style="flex:1; text-align:left;">
              <option value="" selected disabled>Select basemap...</option>
              ${basemapOptions}
            </select>
            <button id="lm-add-basemap" class="btn primary">Add</button>
          </div>
          <div class="lm-title" style="margin-top:10px;">Add Overlay</div>
          <div class="row lm-add-row">
            <select id="lm-overlay-select" style="flex:1; text-align:left;">
              <option value="" selected disabled>Select overlay...</option>
              ${overlayOptions}
            </select>
            <button id="lm-add-overlay" class="btn">Add</button>
          </div>
          <div class="lm-title" style="margin-top:10px;">Raster Overlay (GeoTIFF)</div>
          <div class="row lm-add-row" style="margin-bottom:6px;">
            <input id="lm-geotiff-url" type="text" placeholder="https://.../overlay.tif" style="flex:1;" />
            <button id="lm-add-geotiff-url" class="btn">Add URL</button>
          </div>
          <div class="row lm-add-row">
            <button id="lm-add-geotiff-file" class="btn">Import GeoTIFF</button>
            <button id="lm-add-geotiff-folder" class="btn">Import GeoTIFF Folder</button>
            <input id="lm-geotiff-file" type="file" accept=".tif,.tiff,.tfw,.tifw,.tiffw,.wld,.prj,.xml" multiple style="display:none;" />
          </div>
          <div class="small" style="margin-top:10px;">
            Active layers are listed in the left panel under Layers.
          </div>
          <div style="margin-top:10px; border:1px solid rgba(148,163,184,.35); border-radius:8px; padding:8px;">
            <div class="row" style="justify-content:space-between; margin-bottom:6px;">
              <div class="small" style="font-weight:700;">GeoTIFF Import Progress</div>
              <div class="small">${esc(progressLabel)} ${esc(`${Math.round(progressWidth)}%`)}</div>
            </div>
            <div style="height:10px; border-radius:999px; background:rgba(30,41,59,.45); overflow:hidden;">
              <div style="height:100%; width:${progressWidth}%; background:linear-gradient(90deg,#22c55e,#16a34a); transition:width 220ms ease;"></div>
            </div>
            <div class="small" style="margin-top:6px;">${esc(importProgress.stage || "No active import")}</div>
            <div class="small" style="margin-top:2px; opacity:.85;">${esc(importProgress.message || "")}</div>
          </div>
          <div style="margin-top:10px; border:1px solid rgba(148,163,184,.35); border-radius:8px; padding:8px;">
            <div class="row" style="justify-content:space-between; margin-bottom:6px;">
              <div class="small" style="font-weight:700;">Backend Raster Job Monitor</div>
              <div class="small">${esc(monitoredRasterJob?.id || monitoredRasterJobId || activeRasterJobId || "none")}</div>
            </div>
            <div class="small">Status: <strong>${esc(monitoredRasterJob?.status || "idle")}</strong> | Stage: ${esc(monitoredRasterJob?.stageLabel || "-")} | Progress: ${esc(String(monitoredRasterJob?.progressPct ?? "-"))}%</div>
            <div class="small" style="margin-top:4px;">${esc(monitoredRasterJob?.message || "No monitored backend job.")}</div>
            ${monitoredRasterJob?.error ? `<div class="small" style="margin-top:4px; color:#ef4444;">Error: ${esc(monitoredRasterJob.error)}</div>` : ""}
            ${monitoredRasterJob?.tileTemplate ? `<div class="small" style="margin-top:4px;">Tile URL: <code>${esc(monitoredRasterJob.tileTemplate)}</code></div>` : ""}
            <div class="row lm-add-row" style="margin-top:8px;">
              <button id="lm-raster-monitor-refresh" class="btn">Refresh Job Status</button>
              <button id="lm-raster-monitor-stop" class="btn">Stop Monitoring</button>
            </div>
          </div>
          ${renderSurveyFilterSection()}
        </div>
      `;
    const basemapSel = containerEl.querySelector("#lm-basemap-select");
    const overlaySel = containerEl.querySelector("#lm-overlay-select");
    const urlEl = containerEl.querySelector("#lm-geotiff-url");
    if (basemapSel && lastState.basemapId && CATALOG.some((c) => c.id === lastState.basemapId && c.kind === "basemap")) {
      basemapSel.value = lastState.basemapId;
    }
    if (overlaySel && lastState.overlayId && CATALOG.some((c) => c.id === lastState.overlayId && c.kind !== "basemap")) {
      overlaySel.value = lastState.overlayId;
    }
    if (urlEl && lastState.geotiffUrl) urlEl.value = lastState.geotiffUrl;
  }

  async function loadSurveyMeta() {
    surveyMeta = { years: [], platforms: [], loading: true, loaded: false };
    if (containerEl) render();
    try {
      const base = `${SURVEY_FS_URL}/query`;
      const qs = "where=1%3D1&returnGeometry=false&returnDistinctValues=true&f=json";
      const [yearsRes, platsRes] = await Promise.all([
        fetch(`${base}?${qs}&outFields=cy&orderByFields=cy`),
        fetch(`${base}?${qs}&outFields=platform&orderByFields=platform`),
      ]);
      const yearsData = await yearsRes.json().catch(() => ({}));
      const platsData = await platsRes.json().catch(() => ({}));
      const years = (yearsData?.features || [])
        .map((f) => Number(f?.attributes?.cy))
        .filter((y) => Number.isFinite(y) && y > 1990)
        .sort((a, b) => a - b);
      const platforms = (platsData?.features || [])
        .map((f) => String(f?.attributes?.platform || "").trim())
        .filter(Boolean)
        .sort();
      surveyMeta = { years, platforms, loading: false, loaded: true };
    } catch (err) {
      console.warn("[OverlayManager] survey meta load failed", err);
      surveyMeta = { years: [], platforms: [], loading: false, loaded: false };
    }
    if (containerEl) render();
    loadSurveyTableData();
  }

  async function loadSurveyTableData() {
    surveyTableData = { ...surveyTableData, loading: true };
    try {
      const where = buildSurveyWhere();
      const url = `${SURVEY_FS_URL}/query?where=${encodeURIComponent(where)}&outFields=objectid,cy,project_number,projectname,locality,platform,snm&returnGeometry=false&orderByFields=cy,projectname&resultRecordCount=500&f=json`;
      const res = await fetch(url);
      const data = await res.json().catch(() => ({}));
      const rows = (data?.features || []).map((f) => f.attributes || {});
      const totalSnm = rows.reduce((sum, r) => sum + (Number(r.snm) || 0), 0);
      surveyTableData = { rows, totalSnm, loading: false, loaded: true, selectedIds: new Set() };
      applySurveyHighlight();
    } catch (err) {
      console.warn("[OverlayManager] survey table load failed", err);
      surveyTableData = { ...surveyTableData, loading: false };
    }
    if (containerEl) render();
  }

  async function exportSurvey(format) {
    const ids = [...surveyTableData.selectedIds];
    const where = ids.length > 0 ? `objectid IN (${ids.join(",")})` : buildSurveyWhere();
    try {
      app.ui.toast("Preparing export…");
      const res = await fetch(`${SURVEY_FS_URL}/query?where=${encodeURIComponent(where)}&outFields=*&f=geojson`);
      const geojson = await res.json();
      const count = geojson?.features?.length || 0;
      if (format === "geojson") {
        downloadSurveyFile(JSON.stringify(geojson, null, 2), "ocs_survey_plans.geojson", "application/geo+json");
      } else {
        downloadSurveyFile(geojsonToKml(geojson), "ocs_survey_plans.kml", "application/vnd.google-earth.kml+xml");
      }
      app.ui.toast(`Exported ${count} feature${count === 1 ? "" : "s"} as ${format.toUpperCase()}`);
    } catch (err) {
      console.warn("[OverlayManager] survey export failed", err);
      app.ui.toast("Export failed");
    }
  }

  function geojsonToKml(geojson) {
    function kesc(v) { return String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }
    function coordStr(ring) { return (ring || []).map(([lo, la]) => `${lo},${la},0`).join(" "); }
    function polyKml(poly) {
      const [outer, ...holes] = poly;
      return `<Polygon><outerBoundaryIs><LinearRing><coordinates>${coordStr(outer)}</coordinates></LinearRing></outerBoundaryIs>${holes.map((h) => `<innerBoundaryIs><LinearRing><coordinates>${coordStr(h)}</coordinates></LinearRing></innerBoundaryIs>`).join("")}</Polygon>`;
    }
    function geomKml(g) {
      if (!g) return "";
      if (g.type === "Polygon") return polyKml(g.coordinates || []);
      if (g.type === "MultiPolygon") return `<MultiGeometry>${(g.coordinates || []).map(polyKml).join("")}</MultiGeometry>`;
      return "";
    }
    const styles = Object.entries(SURVEY_YEAR_COLORS).map(([yr, c]) => {
      const fill = c.fill.slice(1);
      const stroke = c.stroke.slice(1);
      return `<Style id="yr${yr}"><PolyStyle><color>99${fill}</color></PolyStyle><LineStyle><color>ff${stroke}</color><width>1.5</width></LineStyle></Style>`;
    }).join("\n");
    const placemarks = (geojson?.features || []).map((f) => {
      const a = f.properties || {};
      const yr = String(a.cy || "");
      const name = a.projectname || a.project_number || "Survey Plan";
      const desc = [
        a.project_number ? `Project #: ${a.project_number}` : "",
        yr ? `Year: ${yr}` : "",
        a.platform ? `Platform: ${a.platform}` : "",
        a.snm ? `Area: ${a.snm} sq nm` : "",
        a.locality ? `Locality: ${a.locality}` : "",
        a.sub_locality ? `Sub-locality: ${a.sub_locality}` : "",
      ].filter(Boolean).join("&#10;");
      return `<Placemark><name>${kesc(name)}</name><description>${kesc(desc)}</description><styleUrl>#yr${yr}</styleUrl>${geomKml(f.geometry)}</Placemark>`;
    }).join("\n");
    return `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>NOAA OCS Survey Plans</name>\n${styles}\n${placemarks}\n</Document></kml>`;
  }

  function downloadSurveyFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { try { a.remove(); URL.revokeObjectURL(url); } catch {} }, 500);
  }

  function buildSurveyWhere() {
    if (surveyYearsEmpty || surveyPlatsEmpty) return "1=0";
    const clauses = [];
    if (surveyYears.size > 0) {
      clauses.push(`cy IN (${[...surveyYears].map((y) => `'${y}'`).join(",")})`);
    }
    if (surveyPlatforms.size > 0) {
      const list = [...surveyPlatforms].map((p) => `'${String(p).replace(/'/g, "''")}'`).join(",");
      clauses.push(`platform IN (${list})`);
    }
    return clauses.length > 0 ? clauses.join(" AND ") : "1=1";
  }

  function applyFilter() {
    if (!surveyLayerId) return;
    app.services.layers.setLayerWhere(surveyLayerId, buildSurveyWhere());
    loadSurveyTableData();
  }

  function applySurveyHighlight() {
    if (!surveyLayerId) return;
    const ids = surveyTableData.selectedIds;
    if (ids.size === 0) {
      app.services.layers.setLayerStyle(surveyLayerId, plannedSurveyStyle);
    } else {
      app.services.layers.setLayerStyle(surveyLayerId, (feature) => {
        const a = feature?.properties || feature?.attributes || {};
        const oid = Number(a.objectid ?? a.OBJECTID ?? 0);
        const base = plannedSurveyStyle(feature);
        return ids.has(oid)
          ? { ...base, fillOpacity: 0.72, weight: 2.8 }
          : { ...base, fillOpacity: 0.09 };
      });
    }
  }

  function updateSurveyLayerTracking() {
    const active = app.services.layers.listLayers()
      .find((l) => l.toolId === "overlay-manager" && String(l.id || "").startsWith("noaa-planned-survey-areas"));
    if (!active) {
      if (surveyLayerId) {
        surveyLayerId = "";
        surveyYears = new Set();
        surveyPlatforms = new Set();
        surveyYearsEmpty = false;
        surveyPlatsEmpty = false;
        surveyMeta = { years: [], platforms: [], loading: false, loaded: false };
        surveyTableData = { rows: [], totalSnm: 0, loading: false, loaded: false, selectedIds: new Set() };
        surveyOpenDropdown = "";
      }
      return;
    }
    if (active.id !== surveyLayerId) {
      surveyLayerId = active.id;
      surveyYears = new Set();
      surveyPlatforms = new Set();
      surveyYearsEmpty = false;
      surveyPlatsEmpty = false;
      surveyTableData = { rows: [], totalSnm: 0, loading: false, loaded: false, selectedIds: new Set() };
      surveyOpenDropdown = "";
      if (!surveyMeta.loaded && !surveyMeta.loading) loadSurveyMeta();
      else loadSurveyTableData();
    }
  }

  function renderSurveyFilterSection() {
    if (!surveyLayerId) return "";
    const layer = app.services.layers.listLayers().find((l) => l.id === surveyLayerId);
    if (!layer) return "";
    const { years, platforms, loading: metaLoading } = surveyMeta;
    const { rows, totalSnm, loading: tableLoading, loaded: tableLoaded, selectedIds } = surveyTableData;

    const yearLabel = surveyYearsEmpty ? "None" : (surveyYears.size === 0 ? "All" : `${surveyYears.size} of ${years.length}`);
    const platLabel = surveyPlatsEmpty ? "None" : (surveyPlatforms.size === 0 ? "All" : `${surveyPlatforms.size} of ${platforms.length}`);
    const yearActive = surveyYears.size > 0 || surveyYearsEmpty;
    const platActive = surveyPlatforms.size > 0 || surveyPlatsEmpty;
    const hasFilter = surveyYears.size > 0 || surveyPlatforms.size > 0 || surveyYearsEmpty || surveyPlatsEmpty;

    const yearPanel = years.length ? `
      <div id="lm-survey-year-panel" style="display:${surveyOpenDropdown === "year" ? "block" : "none"};position:absolute;top:calc(100% + 3px);left:0;z-index:300;background:rgba(15,23,42,.97);border:1px solid rgba(148,163,184,.22);border-radius:6px;padding:8px;min-width:160px;max-height:240px;overflow-y:auto;box-shadow:0 4px 16px rgba(0,0,0,.5);">
        <div style="display:flex;gap:6px;margin-bottom:6px;">
          <button id="lm-survey-year-all" class="btn" style="font-size:10px;padding:2px 8px;">All</button>
          <button id="lm-survey-year-none" class="btn" style="font-size:10px;padding:2px 8px;">None</button>
          <button id="lm-survey-year-clear" class="btn" style="font-size:10px;padding:2px 8px;">Clear</button>
        </div>
        ${years.map((yr) => {
          const c = SURVEY_YEAR_COLORS[yr] || { fill: "#94a3b8", stroke: "#64748b" };
          const checked = !surveyYearsEmpty && (surveyYears.size === 0 || surveyYears.has(yr));
          return `<label style="display:flex;align-items:center;gap:6px;padding:3px 0;font-size:11px;cursor:pointer;white-space:nowrap;">
            <input type="checkbox" data-survey-year="${yr}" ${checked ? "checked" : ""} style="accent-color:#38bdf8;margin:0;" />
            <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${c.fill};border:1px solid ${c.stroke};flex-shrink:0;"></span>
            <span>${yr}</span>
          </label>`;
        }).join("")}
      </div>` : "";

    const platPanel = platforms.length ? `
      <div id="lm-survey-plat-panel" style="display:${surveyOpenDropdown === "platform" ? "block" : "none"};position:absolute;top:calc(100% + 3px);left:0;z-index:300;background:rgba(15,23,42,.97);border:1px solid rgba(148,163,184,.22);border-radius:6px;padding:8px;min-width:200px;max-height:240px;overflow-y:auto;box-shadow:0 4px 16px rgba(0,0,0,.5);">
        <div style="display:flex;gap:6px;margin-bottom:6px;">
          <button id="lm-survey-plat-all" class="btn" style="font-size:10px;padding:2px 8px;">All</button>
          <button id="lm-survey-plat-none" class="btn" style="font-size:10px;padding:2px 8px;">None</button>
          <button id="lm-survey-plat-clear" class="btn" style="font-size:10px;padding:2px 8px;">Clear</button>
        </div>
        ${platforms.map((p) => {
          const checked = !surveyPlatsEmpty && (surveyPlatforms.size === 0 || surveyPlatforms.has(p));
          return `<label style="display:flex;align-items:center;gap:6px;padding:3px 0;font-size:11px;cursor:pointer;white-space:nowrap;">
            <input type="checkbox" data-survey-platform="${esc(p)}" ${checked ? "checked" : ""} style="accent-color:#38bdf8;margin:0;" />
            <span>${esc(p)}</span>
          </label>`;
        }).join("")}
      </div>` : "";

    const allRowsSelected = rows.length > 0 && rows.length === selectedIds.size;
    const tableHtml = tableLoaded && rows.length ? `
      <div style="border:1px solid rgba(148,163,184,.18);border-radius:6px;overflow:hidden;margin:6px 0;">
        <div style="max-height:200px;overflow-y:auto;">
          <table style="width:100%;border-collapse:collapse;font-size:10px;">
            <thead>
              <tr style="background:rgba(2,6,23,.7);position:sticky;top:0;z-index:1;">
                <th style="width:18px;padding:4px 5px;"><input type="checkbox" id="lm-survey-select-all" ${allRowsSelected ? "checked" : ""} style="accent-color:#38bdf8;margin:0;" /></th>
                <th style="text-align:left;padding:4px 5px;color:var(--muted);font-weight:600;white-space:nowrap;">Year</th>
                <th style="text-align:left;padding:4px 5px;color:var(--muted);font-weight:600;">Project</th>
                <th style="text-align:left;padding:4px 5px;color:var(--muted);font-weight:600;white-space:nowrap;">Platform</th>
                <th style="text-align:right;padding:4px 5px;color:var(--muted);font-weight:600;white-space:nowrap;">Sq nm</th>
              </tr>
            </thead>
            <tbody>
              ${rows.slice(0, 300).map((r) => {
                const oid = Number(r.objectid ?? r.OBJECTID ?? 0);
                const sel = selectedIds.has(oid);
                const yr = Number(r.cy);
                const c = SURVEY_YEAR_COLORS[yr] || { fill: "#94a3b8" };
                return `<tr data-survey-row="${oid}" style="cursor:pointer;border-top:1px solid rgba(148,163,184,.07);${sel ? "background:rgba(56,189,248,.12);" : ""}">
                  <td style="padding:3px 5px;"><input type="checkbox" data-survey-row-check="${oid}" ${sel ? "checked" : ""} style="accent-color:#38bdf8;margin:0;" /></td>
                  <td style="padding:3px 5px;white-space:nowrap;">
                    <span style="display:inline-block;width:6px;height:6px;border-radius:1px;background:${c.fill};margin-right:3px;vertical-align:middle;flex-shrink:0;"></span>${esc(String(r.cy || ""))}
                  </td>
                  <td style="padding:3px 5px;max-width:110px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${esc(r.projectname || "")}">${esc(r.projectname || r.project_number || "—")}</td>
                  <td style="padding:3px 5px;white-space:nowrap;">${esc(r.platform || "—")}</td>
                  <td style="padding:3px 5px;text-align:right;white-space:nowrap;">${Number.isFinite(Number(r.snm)) ? Number(r.snm).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—"}</td>
                </tr>`;
              }).join("")}
              ${rows.length > 300 ? `<tr><td colspan="5" style="padding:4px 6px;text-align:center;color:var(--muted);font-size:9px;">… ${rows.length - 300} more rows</td></tr>` : ""}
            </tbody>
          </table>
        </div>
      </div>` : "";

    const exportLabel = selectedIds.size > 0 ? `${selectedIds.size} selected` : `all ${rows.length}`;
    const snmFmt = totalSnm.toLocaleString(undefined, { maximumFractionDigits: 1 });

    return `
      <div style="margin-top:10px;border:1px solid rgba(148,163,184,.35);border-radius:8px;padding:8px;">
        <div class="row" style="justify-content:space-between;align-items:center;margin-bottom:6px;">
          <div class="small" style="font-weight:700;">OCS Survey Plans</div>
          ${hasFilter ? `<button id="lm-survey-filter-reset" class="btn" style="font-size:9px;padding:1px 6px;">Reset</button>` : ""}
        </div>

        ${metaLoading ? `<div class="small" style="opacity:.7;margin-bottom:6px;">Loading filters…</div>` : `
        <div class="row" style="gap:6px;margin-bottom:6px;flex-wrap:wrap;">
          ${years.length ? `<div style="position:relative;">
            <button id="lm-survey-year-toggle" class="btn" style="font-size:10px;padding:3px 10px;${yearActive ? "color:#38bdf8;border-color:rgba(56,189,248,.4);" : ""}">Year: ${yearLabel} ▾</button>
            ${yearPanel}
          </div>` : ""}
          ${platforms.length ? `<div style="position:relative;">
            <button id="lm-survey-plat-toggle" class="btn" style="font-size:10px;padding:3px 10px;${platActive ? "color:#38bdf8;border-color:rgba(56,189,248,.4);" : ""}">Platform: ${platLabel} ▾</button>
            ${platPanel}
          </div>` : ""}
        </div>`}

        ${tableLoading ? `<div class="small" style="opacity:.7;margin-bottom:4px;">Loading table…</div>` : ""}
        ${tableLoaded ? `<div class="small" style="color:var(--muted);margin-bottom:4px;">${rows.length} feature${rows.length !== 1 ? "s" : ""} · ${snmFmt} sq nm total${selectedIds.size > 0 ? ` · <strong style="color:#38bdf8;">${selectedIds.size} selected</strong>` : ""}</div>` : ""}

        ${tableHtml}

        ${tableLoaded && rows.length ? `<div class="row" style="gap:6px;margin-top:4px;">
          <button id="lm-survey-export-geojson" class="btn" style="font-size:10px;">↓ GeoJSON (${exportLabel})</button>
          <button id="lm-survey-export-kml" class="btn" style="font-size:10px;">↓ KML (${exportLabel})</button>
        </div>` : ""}
      </div>
    `;
  }

  function addCatalogLayer(def) {
    try {
      let layerDef = { ...def, id: `${def.id}-${Math.random().toString(16).slice(2, 6)}` };
      if (typeof def.buildLeaflet === "function") {
        layerDef = { ...layerDef, type: "leaflet-group", leaflet: def.buildLeaflet() };
        delete layerDef.buildLeaflet;
      }
      app.services.layers.addLayer("overlay-manager", layerDef);
      app.ui.toast(`Added: ${def.name}`);
    } catch (err) {
      console.error("[OverlayManager] add layer failed", err);
      app.ui.toast(`Failed to add layer: ${def.name}`);
    }
  }

  function onClick(e) {
    const addBasemapBtn = e.target?.closest?.("#lm-add-basemap");
    if (addBasemapBtn) {
      const sel = containerEl?.querySelector("#lm-basemap-select");
      const id = sel?.value;
      const def = CATALOG.find((x) => x.id === id);
      if (!def) return;
      addCatalogLayer(def);
      pullFormState();
      return;
    }

    const addOverlayBtn = e.target?.closest?.("#lm-add-overlay");
    if (addOverlayBtn) {
      const sel = containerEl?.querySelector("#lm-overlay-select");
      const id = sel?.value;
      const def = CATALOG.find((x) => x.id === id);
      if (!def) return;
      addCatalogLayer(def);
      pullFormState();
      return;
    }

    const addGeoTiffUrlBtn = e.target?.closest?.("#lm-add-geotiff-url");
    if (addGeoTiffUrlBtn) {
      const input = containerEl?.querySelector("#lm-geotiff-url");
      const url = String(input?.value || "").trim();
      if (!url) {
        app.ui.toast("Enter a GeoTIFF URL first");
        return;
      }
      try {
        app.services.layers.addLayer("overlay-manager", {
          id: `geotiff-url-${Math.random().toString(16).slice(2, 6)}`,
          name: `GeoTIFF: ${url.split("/").pop() || "overlay"}`,
          kind: "overlay",
          type: "geotiff-url",
          url,
          options: { resolution: 256 },
          opacity: 0.85,
          visible: true,
          volatile: false,
          meta: { sourceType: "url" },
        });
        app.ui.toast("GeoTIFF URL added");
        if (input) input.value = "";
      } catch (err) {
        console.error("[OverlayManager] add geotiff url failed", err);
        app.ui.toast("Failed to add GeoTIFF URL");
      }
      pullFormState();
      return;
    }

    const addGeoTiffFileBtn = e.target?.closest?.("#lm-add-geotiff-file");
    if (addGeoTiffFileBtn) {
      containerEl?.querySelector("#lm-geotiff-file")?.click();
      return;
    }

    const addGeoTiffFolderBtn = e.target?.closest?.("#lm-add-geotiff-folder");
    if (addGeoTiffFolderBtn) {
      importGeoTiffFolder();
      return;
    }

    if (e.target?.closest?.("#lm-raster-monitor-refresh")) {
      pollRasterJob({ force: true });
      return;
    }
    if (e.target?.closest?.("#lm-raster-monitor-stop")) {
      activeRasterJobId = "";
      monitoredRasterJobId = "";
      monitoredRasterJob = null;
      stopRasterJobPolling();
      persistRasterMonitor();
      render();
      return;
    }

    if (e.target?.closest?.("#lm-survey-filter-reset")) {
      surveyYears = new Set();
      surveyPlatforms = new Set();
      surveyYearsEmpty = false;
      surveyPlatsEmpty = false;
      surveyOpenDropdown = "";
      applyFilter();
      render();
      return;
    }

    if (e.target?.closest?.("#lm-survey-year-toggle")) {
      surveyOpenDropdown = surveyOpenDropdown === "year" ? "" : "year";
      render();
      return;
    }
    if (e.target?.closest?.("#lm-survey-plat-toggle")) {
      surveyOpenDropdown = surveyOpenDropdown === "platform" ? "" : "platform";
      render();
      return;
    }
    if (e.target?.closest?.("#lm-survey-year-all")) {
      surveyYears = new Set();
      surveyYearsEmpty = false;
      applyFilter();
      surveyOpenDropdown = "year";
      render();
      return;
    }
    if (e.target?.closest?.("#lm-survey-year-none")) {
      surveyYears = new Set();
      surveyYearsEmpty = true;
      applyFilter();
      surveyOpenDropdown = "year";
      render();
      return;
    }
    if (e.target?.closest?.("#lm-survey-year-clear")) {
      surveyYears = new Set(surveyMeta.years);
      surveyYearsEmpty = false;
      applyFilter();
      surveyOpenDropdown = "year";
      render();
      return;
    }
    if (e.target?.closest?.("#lm-survey-plat-all")) {
      surveyPlatforms = new Set();
      surveyPlatsEmpty = false;
      applyFilter();
      surveyOpenDropdown = "platform";
      render();
      return;
    }
    if (e.target?.closest?.("#lm-survey-plat-none")) {
      surveyPlatforms = new Set();
      surveyPlatsEmpty = true;
      applyFilter();
      surveyOpenDropdown = "platform";
      render();
      return;
    }
    if (e.target?.closest?.("#lm-survey-plat-clear")) {
      surveyPlatforms = new Set(surveyMeta.platforms);
      surveyPlatsEmpty = false;
      applyFilter();
      surveyOpenDropdown = "platform";
      render();
      return;
    }
    if (e.target?.closest?.("#lm-survey-export-geojson")) {
      exportSurvey("geojson");
      return;
    }
    if (e.target?.closest?.("#lm-survey-export-kml")) {
      exportSurvey("kml");
      return;
    }
    const rowEl = e.target?.closest?.("[data-survey-row]");
    if (rowEl && !e.target?.hasAttribute?.("data-survey-row-check") && e.target?.id !== "lm-survey-select-all") {
      const oid = Number(rowEl.getAttribute("data-survey-row") || 0);
      if (oid) {
        if (surveyTableData.selectedIds.has(oid)) surveyTableData.selectedIds.delete(oid);
        else surveyTableData.selectedIds.add(oid);
        applySurveyHighlight();
        render();
      }
      return;
    }
    if (surveyOpenDropdown && !e.target?.closest?.("#lm-survey-year-toggle") && !e.target?.closest?.("#lm-survey-year-panel") && !e.target?.closest?.("#lm-survey-plat-toggle") && !e.target?.closest?.("#lm-survey-plat-panel")) {
      surveyOpenDropdown = "";
      render();
    }
  }

  function mount(nextContainer) {
    restoreRasterMonitor();
    if (!migratedNoaaWmsOnce) {
      migratedNoaaWmsOnce = true;
      let changed = 0;
      const layers = app.services.layers.listLayers();
      layers
        .filter((l) => l?.toolId === "overlay-manager" && l?.type === "wms" && (l?.id === "noaa-chart-display-traditional-wms" || l?.id === "noaa-ecdis-display"))
        .forEach((l) => {
          const currentLayers = String(l?.options?.layers || "").replace(/\s+/g, "");
          if (currentLayers === NOAA_CLEAN_WMS_LAYERS) return;
          try {
            app.services.layers.removeLayer(l.id);
            app.services.layers.addLayer("overlay-manager", {
              ...l,
              options: {
                ...(l.options || {}),
                layers: NOAA_CLEAN_WMS_LAYERS,
              },
            });
            changed += 1;
          } catch (err) {
            console.warn("[OverlayManager] NOAA WMS cleanup migration failed", err);
          }
        });

      layers
        .filter((l) => l?.toolId === "overlay-manager" && String(l?.id || "").startsWith("etopo-hillshade"))
        .forEach((l) => {
          const currentType = String(l?.type || "");
          const currentUrl = String(l?.url || "");
          const currentLayers = String(l?.options?.layers || "").replace(/\s+/g, "");
          const currentVersion = String(l?.options?.version || "");
          const currentCrsCode = String(l?.options?.crsCode || "");
          const needsMigration = currentType !== "wms"
            || currentUrl !== ETOPO_HILLSHADE_WMS_URL
            || currentLayers !== ETOPO_HILLSHADE_WMS_LAYERS
            || currentVersion !== "1.1.1"
            || currentCrsCode !== "EPSG:4326";
          if (!needsMigration) return;
          try {
            app.services.layers.removeLayer(l.id);
            app.services.layers.addLayer("overlay-manager", {
              ...l,
              type: "wms",
              url: ETOPO_HILLSHADE_WMS_URL,
              options: {
                layers: ETOPO_HILLSHADE_WMS_LAYERS,
                format: "image/png",
                transparent: true,
                version: "1.1.1",
                crsCode: "EPSG:4326",
                maxZoom: 24,
                attribution: "NOAA NCEI",
                opacity: Number.isFinite(Number(l?.options?.opacity)) ? Number(l.options.opacity) : 0.8,
              },
            });
            changed += 1;
          } catch (err) {
            console.warn("[OverlayManager] ETOPO hillshade migration failed", err);
          }
        });

      if (changed > 0) app.ui.toast(`Updated ${changed} overlay layer${changed === 1 ? "" : "s"} with latest default rendering.`);
    }

    if (containerEl === nextContainer) {
      render();
      return;
    }
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("change", onChange);
    }
    containerEl = nextContainer;
    updateSurveyLayerTracking();
    render();
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("change", onChange);
    if (!layersChangedOff) {
      layersChangedOff = app.events.on("layers:changed", () => {
        updateSurveyLayerTracking();
        render();
      });
    }
  }

  function onChange(e) {
    const el = e.target;
    if (el?.id === "lm-geotiff-file") {
      importGeoTiffFiles([...(el.files || [])]);
      el.value = "";
      return;
    }
    if (el?.dataset?.surveyYear !== undefined && el.type === "checkbox") {
      surveyYearsEmpty = false;
      const yr = Number(el.dataset.surveyYear);
      const { years } = surveyMeta;
      if (!el.checked) {
        if (surveyYears.size === 0) years.forEach((y) => { if (y !== yr) surveyYears.add(y); });
        else surveyYears.delete(yr);
      } else {
        surveyYears.add(yr);
        if (surveyYears.size === years.length) surveyYears = new Set();
      }
      applyFilter();
      surveyOpenDropdown = "year";
      render();
      return;
    }
    if (el?.dataset?.surveyPlatform !== undefined && el.type === "checkbox") {
      surveyPlatsEmpty = false;
      const p = el.dataset.surveyPlatform;
      const { platforms } = surveyMeta;
      if (!el.checked) {
        if (surveyPlatforms.size === 0) platforms.forEach((x) => { if (x !== p) surveyPlatforms.add(x); });
        else surveyPlatforms.delete(p);
      } else {
        surveyPlatforms.add(p);
        if (surveyPlatforms.size === platforms.length) surveyPlatforms = new Set();
      }
      applyFilter();
      surveyOpenDropdown = "platform";
      render();
      return;
    }
    if (el?.dataset?.surveyRowCheck !== undefined) {
      const oid = Number(el.dataset.surveyRowCheck || 0);
      if (oid) {
        if (el.checked) surveyTableData.selectedIds.add(oid);
        else surveyTableData.selectedIds.delete(oid);
        applySurveyHighlight();
        render();
      }
      return;
    }
    if (el?.id === "lm-survey-select-all") {
      if (el.checked) {
        surveyTableData.rows.forEach((r) => {
          const oid = Number(r.objectid ?? r.OBJECTID ?? 0);
          if (oid) surveyTableData.selectedIds.add(oid);
        });
      } else {
        surveyTableData.selectedIds.clear();
      }
      applySurveyHighlight();
      render();
      return;
    }
    pullFormState();
  }

  async function importGeoTiffFiles(files) {
    const allFiles = Array.isArray(files) ? files.filter(Boolean) : [];
    const startTs = Date.now();
    setImportProgress({
      active: true,
      complete: false,
      failed: false,
      progressPct: 3,
      stage: "Scanning selected files",
      message: `${allFiles.length} file(s) selected`,
      startedAt: startTs,
      finishedAt: 0,
    });
    debugNotify(app, `GeoTIFF picker: ${allFiles.length} file(s) selected`);
    const tiffFiles = allFiles.filter(isGeoTiffFile);
    if (!tiffFiles.length) {
      debugNotify(app, "Select at least one .tif/.tiff file.");
      setImportProgress({
        active: false,
        complete: false,
        failed: true,
        progressPct: 100,
        stage: "No GeoTIFF files found",
        message: "Select at least one .tif or .tiff file.",
        finishedAt: Date.now(),
      });
      return;
    }
    const sidecars = buildSidecarLookup(allFiles.filter((file) => !isGeoTiffFile(file)));
    setImportProgress({
      progressPct: 10,
      stage: "Matched sidecar metadata",
      message: `${tiffFiles.length} GeoTIFF file(s) queued`,
    });
    let completed = 0;
    let backendJobsStarted = 0;
    for (const file of tiffFiles) {
      const baseName = normalizeBaseName(file.name);
      const worldFile = sidecars.worldByBase.get(baseName) || null;
      const prjFile = sidecars.prjByBase.get(baseName) || null;
      const metadataFile = sidecars.metadataByBase.get(baseName) || null;
      try {
        const fileStartPct = 10 + Math.round((completed / tiffFiles.length) * 80);
        setImportProgress({
          progressPct: fileStartPct,
          stage: "Preparing raster import",
          message: file.name,
        });
        const url = URL.createObjectURL(file);
        const layerId = `geotiff-file-${Math.random().toString(16).slice(2, 6)}`;
        const options = { resolution: 256 };
        let worldFileAffine = null;
        let worldFileRaw = "";
        if (worldFile) {
          setImportProgress({
            progressPct: Math.min(88, fileStartPct + 8),
            stage: "Reading world file",
            message: worldFile.name,
          });
          worldFileRaw = await readFileAsText(worldFile);
          worldFileAffine = parseWorldFileText(worldFileRaw);
          if (worldFileAffine) options.worldFileAffine = worldFileAffine;
        }
        let projectionCode = "";
        let projectionDef = "";
        if (prjFile) {
          setImportProgress({
            progressPct: Math.min(90, fileStartPct + 10),
            stage: "Reading projection file",
            message: prjFile.name,
          });
          projectionDef = String(await readFileAsText(prjFile)).trim();
          projectionCode = inferProjectionCodeFromWkt(projectionDef);
          if (projectionCode && projectionDef) {
            options.projectionCode = projectionCode;
            options.projectionDef = projectionDef;
          }
        } else if (metadataFile) {
          setImportProgress({
            progressPct: Math.min(90, fileStartPct + 10),
            stage: "Reading metadata sidecar",
            message: metadataFile.name,
          });
          const metadataText = String(await readFileAsText(metadataFile)).trim();
          const inferred = inferProjectionFromMetadataXml(metadataText);
          projectionDef = inferred.projectionDef || "";
          projectionCode = inferred.projectionCode || "";
          if (Array.isArray(inferred.geographicBounds) && inferred.geographicBounds.length === 4) {
            options.geographicBounds = inferred.geographicBounds;
          }
          if (projectionDef) {
            options.projectionDef = projectionDef;
            if (projectionCode) options.projectionCode = projectionCode;
          }
        }
        app.services.layers.addLayer("overlay-manager", {
          id: layerId,
          name: `GeoTIFF: ${file.name}`,
          kind: "overlay",
          type: "geotiff-url",
          url,
          options: { ...options, autoZoom: true },
          opacity: 0.85,
          visible: true,
          volatile: true,
          meta: {
            sourceType: "local-file",
            fileName: file.name,
            fileSize: Number(file.size) || 0,
            fileType: String(file.type || "image/tiff"),
            worldFileName: worldFile?.name || "",
            worldFileAffine: worldFileAffine || null,
            worldFileRaw: worldFileRaw || "",
            prjFileName: prjFile?.name || "",
            metadataFileName: metadataFile?.name || "",
            projectionCode: projectionCode || "",
            projectionDef: projectionDef || "",
            geographicBounds: Array.isArray(options.geographicBounds) ? options.geographicBounds : null,
          },
        });
        debugNotify(app, `Layer created: ${file.name}`);
        try {
          const uploaded = await uploadRasterSource(file);
          if (uploaded?.sourcePath) {
            await beginRasterJobTracking({
              datasetName: file.name,
              fileCount: 1,
              sourcePath: uploaded.sourcePath,
              layerId,
              layerName: `GeoTIFF: ${file.name}`,
            });
            backendJobsStarted += 1;
          }
        } catch (uploadErr) {
          console.warn("[OverlayManager] raster upload failed", uploadErr);
        }
        setImportProgress({
          progressPct: Math.min(95, fileStartPct + 14),
          stage: "Creating map layer",
          message: file.name,
        });
        readFileAsDataUrl(file)
          .then((dataUrl) => {
            app.services.layers.updateLayer(layerId, {
              meta: {
                sourceType: "local-file",
                fileName: file.name,
                fileSize: Number(file.size) || 0,
                fileType: String(file.type || "image/tiff"),
                worldFileName: worldFile?.name || "",
                worldFileAffine: worldFileAffine || null,
                worldFileRaw: worldFileRaw || "",
                prjFileName: prjFile?.name || "",
                metadataFileName: metadataFile?.name || "",
                projectionCode: projectionCode || "",
                projectionDef: projectionDef || "",
                geographicBounds: Array.isArray(options.geographicBounds) ? options.geographicBounds : null,
                embeddedDataUrl: dataUrl,
              },
            });
          })
          .catch((err) => {
            console.warn("[OverlayManager] file embed prep failed", err);
          });
        const parts = [`Imported: ${file.name}`];
        if (worldFileAffine) parts.push(`TFW ${worldFile.name}`);
        if (projectionCode || projectionDef) parts.push(`CRS ${prjFile?.name || metadataFile?.name || "loaded"}`);
        debugNotify(app, parts.join(" | "));
        completed += 1;
        const donePct = 10 + Math.round((completed / tiffFiles.length) * 88);
        setImportProgress({
          progressPct: Math.min(98, Math.max(donePct, Number(importProgress.progressPct) || 0)),
          stage: "Layer queued for rendering",
          message: `${completed}/${tiffFiles.length} complete`,
        });
      } catch (err) {
        console.error("[OverlayManager] import geotiff file failed", err);
        debugNotify(app, `Failed to import: ${file?.name || "GeoTIFF"}`);
        setImportProgress({
          active: false,
          complete: false,
          failed: true,
          progressPct: 100,
          stage: "Import failed",
          message: String(err?.message || err || "Unknown error"),
          finishedAt: Date.now(),
        });
        return;
      }
    }
    setImportProgress({
      active: false,
      complete: true,
      failed: false,
      progressPct: 100,
      stage: "All imports submitted",
      message: `${completed} GeoTIFF file(s) imported in ${Math.max(1, Math.round((Date.now() - startTs) / 1000))}s`,
      finishedAt: Date.now(),
    });
    if (!backendJobsStarted) {
      await completeRasterJob(true, `${completed} GeoTIFF file(s) imported.`);
    }
  }

  async function importGeoTiffFolder() {
    const showDirectoryPicker = globalThis?.showDirectoryPicker;
    if (typeof showDirectoryPicker !== "function") {
      debugNotify(app, "Folder import requires a Chromium browser with File System Access API.");
      return;
    }
    try {
      const dir = await showDirectoryPicker({ mode: "read" });
      const files = [];
      for await (const entry of dir.values()) {
        if (entry?.kind !== "file") continue;
        const file = await entry.getFile();
        files.push(file);
      }
      if (!files.length) {
        debugNotify(app, "Selected folder has no files.");
        return;
      }
      debugNotify(app, `Folder selected: ${files.length} file(s) found`);
      await importGeoTiffFiles(files);
    } catch (err) {
      if (String(err?.name || "") === "AbortError") return;
      console.error("[OverlayManager] folder import failed", err);
      debugNotify(app, "GeoTIFF folder import failed.");
    }
  }

  function snapshot(options = {}) {
    pullFormState();
    const dataPolicy = String(options?.dataPolicy || "reference");
    return {
      ...lastState,
      overlayLayers: collectOverlayLayersForProject(dataPolicy),
    };
  }

  function restore(data) {
    if (!data || typeof data !== "object") return;
    unresolvedRefs.clear();
    app.services.layers.removeLayersByTool("overlay-manager");
    lastState = {
      basemapId: String(data.basemapId || ""),
      overlayId: String(data.overlayId || ""),
      geotiffUrl: String(data.geotiffUrl || ""),
    };
    const defs = Array.isArray(data.overlayLayers) ? data.overlayLayers : [];
    let restored = 0;
    let unresolved = 0;
    defs.forEach((l) => {
      if (!l || typeof l !== "object" || !l.type) return;
      let url = typeof l.url === "string" ? l.url : "";
      let volatile = false;
      const meta = { ...(l.meta || {}) };
      if (l.type === "geotiff-url" && !url && typeof l.embeddedDataUrl === "string") {
        const objectUrl = dataUrlToObjectUrl(l.embeddedDataUrl);
        if (objectUrl) {
          url = objectUrl;
          volatile = true;
        }
      }
      if (!url && l.type !== "managed") {
        if (l.type === "leaflet-group") return;
        unresolved += 1;
        const refId = String(l.id || `overlay-ref-${Math.random().toString(16).slice(2, 6)}`);
        unresolvedRefs.set(refId, {
          id: refId,
          name: l.name || "Overlay",
          def: l,
        });
        return;
      }
      try {
        app.services.layers.addLayer("overlay-manager", {
          id: l.id || `overlay-${Math.random().toString(16).slice(2, 6)}`,
          name: l.name || "Overlay",
          group: l.group ?? null,
          kind: l.kind || "overlay",
          type: l.type,
          url,
          options: l.options || {},
          opacity: Number.isFinite(Number(l.opacity)) ? Number(l.opacity) : 1,
          visible: l.visible !== false,
          volatile,
          meta,
        });
        restored += 1;
      } catch (err) {
        console.error("[OverlayManager] restore layer failed", err);
      }
    });
    if (restored || unresolved) {
      app.ui.toast(`Overlay restore: ${restored} restored${unresolved ? `, ${unresolved} unresolved` : ""}`);
    }
    render();
    return {
      restored,
      unresolved: [...unresolvedRefs.values()].map((r) => ({
        toolId: "overlay-manager",
        referenceId: r.id,
        label: r.name,
        kind: "geotiff",
        hint: "Select the local GeoTIFF file to relink.",
        expectedFileName: String(r?.def?.meta?.fileName || "").trim() || null,
      })),
    };
  }

  async function resolveReference(referenceId, file) {
    const ref = unresolvedRefs.get(String(referenceId || ""));
    if (!ref) throw new Error("Reference not found.");
    if (!file) throw new Error("Choose a file to relink.");
    const def = ref.def || {};
    const objectUrl = URL.createObjectURL(file);
    const layerId = def.id || `geotiff-file-${Math.random().toString(16).slice(2, 6)}`;
    app.services.layers.addLayer("overlay-manager", {
      id: layerId,
      name: def.name || `GeoTIFF: ${file.name}`,
      group: def.group ?? null,
      kind: def.kind || "overlay",
      type: def.type || "geotiff-url",
      url: objectUrl,
      options: { ...(def.options || { resolution: 256 }), autoZoom: true },
      opacity: Number.isFinite(Number(def.opacity)) ? Number(def.opacity) : 0.85,
      visible: def.visible !== false,
      volatile: true,
      meta: {
        ...(def.meta || {}),
        sourceType: "local-file",
        fileName: file.name,
        fileSize: Number(file.size) || 0,
        fileType: String(file.type || "image/tiff"),
      },
    });
    try {
      const dataUrl = await readFileAsDataUrl(file);
      app.services.layers.updateLayer(layerId, {
        meta: {
          ...(def.meta || {}),
          sourceType: "local-file",
          fileName: file.name,
          fileSize: Number(file.size) || 0,
          fileType: String(file.type || "image/tiff"),
          embeddedDataUrl: dataUrl,
        },
      });
    } catch {}
    unresolvedRefs.delete(ref.id);
    app.ui.toast(`Relinked: ${file.name}`);
    return { restored: 1 };
  }

  function destroy() {
    stopRasterJobPolling();
    layersChangedOff?.();
    layersChangedOff = null;
    if (!containerEl) return;
    containerEl.removeEventListener("click", onClick);
    containerEl.removeEventListener("change", onChange);
    containerEl = null;
  }

  function setImportProgress(patch = {}) {
    importProgress = {
      ...importProgress,
      ...(patch && typeof patch === "object" ? patch : {}),
    };
    if (containerEl) render();
  }

  async function beginRasterJobTracking(payload) {
    stopRasterJobPolling();
    activeRasterJobId = "";
    try {
      const res = await fetch("/api/raster/jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      });
      const body = await res.json().catch(() => ({}));
      const id = String(body?.job?.id || "").trim();
      if (!res.ok || !id) return;
      activeRasterJobId = id;
      monitoredRasterJobId = id;
      if (payload?.layerId) {
        rasterJobLayerMap.set(id, {
          layerId: String(payload.layerId),
          layerName: String(payload.layerName || payload.datasetName || "Raster"),
        });
      }
      persistRasterMonitor();
      pollRasterJob({ force: true });
      rasterJobPollTimer = setInterval(pollRasterJob, 800);
    } catch {}
  }

  function stopRasterJobPolling() {
    if (rasterJobPollTimer) {
      clearInterval(rasterJobPollTimer);
      rasterJobPollTimer = null;
    }
  }

  async function pollRasterJob(options = {}) {
    const targetJobId = String(activeRasterJobId || monitoredRasterJobId || "").trim();
    if (!targetJobId) return;
    try {
      const res = await fetch(`/api/raster/jobs/${encodeURIComponent(targetJobId)}`);
      const body = await res.json().catch(() => ({}));
      const job = body?.job;
      if (!res.ok || !job) return;
      monitoredRasterJobId = String(job.id || targetJobId);
      monitoredRasterJob = {
        id: String(job.id || ""),
        status: String(job.status || ""),
        stageLabel: String(job.stageLabel || ""),
        progressPct: Number(job.progressPct),
        message: String(job.message || ""),
        error: String(job.error || ""),
        tileTemplate: String(job.tileTemplate || ""),
      };
      persistRasterMonitor();
      const pct = Number(job.progressPct);
      const safePct = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0;
      setImportProgress({
        progressPct: Math.max(Number(importProgress.progressPct) || 0, safePct),
        stage: String(job.stageLabel || importProgress.stage || ""),
        message: String(job.message || importProgress.message || ""),
      });
      if (["completed", "failed", "canceled"].includes(String(job.status || "").toLowerCase())) {
        if (String(job.status || "").toLowerCase() === "completed" && monitoredRasterJob.tileTemplate) {
          tryPromoteLayerToTiles(monitoredRasterJob.id, monitoredRasterJob.tileTemplate);
        }
        stopRasterJobPolling();
      }
      if (options?.force && containerEl) render();
    } catch {}
  }

  async function completeRasterJob(success, message) {
    if (!activeRasterJobId) return;
    try {
      await fetch("/api/raster/jobs/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: activeRasterJobId, success: !!success, message: String(message || "") }),
      });
    } catch {}
    stopRasterJobPolling();
    monitoredRasterJobId = String(activeRasterJobId || monitoredRasterJobId || "").trim();
    activeRasterJobId = "";
    persistRasterMonitor();
  }

  async function uploadRasterSource(file) {
    const res = await fetch("/api/raster/upload", {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "x-file-name": String(file?.name || "upload.tif"),
      },
      body: file,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(String(body?.error || "upload_failed"));
    return body;
  }

  function tryPromoteLayerToTiles(jobId, tileTemplate) {
    const link = rasterJobLayerMap.get(String(jobId || ""));
    if (!link || !tileTemplate) return;
    const existing = app.services.layers.listLayers().find((l) => String(l.id || "") === String(link.layerId || ""));
    if (!existing) return;
    try {
      app.services.layers.removeLayer(existing.id);
      app.services.layers.addLayer("overlay-manager", {
        id: existing.id,
        name: `${link.layerName} (Tiled)`,
        kind: existing.kind || "overlay",
        type: "xyz",
        url: tileTemplate,
        options: {
          maxZoom: 22,
          minZoom: 0,
          tms: false,
        },
        opacity: Number.isFinite(Number(existing.opacity)) ? Number(existing.opacity) : 0.85,
        visible: existing.visible !== false,
        volatile: false,
        meta: {
          ...(existing.meta || {}),
          sourceType: "raster-tiles",
          tileTemplate,
          rasterJobId: String(jobId),
          cogPath: (() => {
            const m = tileTemplate.match(/[?&]url=([^&{]+)/);
            return m ? decodeURIComponent(m[1]) : null;
          })(),
        },
      });
      debugNotify(app, `Promoted to tile service: ${link.layerName}`);
    } catch (err) {
      console.warn("[OverlayManager] tile promotion failed", err);
    }
  }

  function persistRasterMonitor() {
    try {
      localStorage.setItem(RASTER_MONITOR_KEY, JSON.stringify({
        activeRasterJobId: String(activeRasterJobId || ""),
        monitoredRasterJobId: String(monitoredRasterJobId || ""),
        monitoredRasterJob: monitoredRasterJob || null,
      }));
    } catch {}
  }

  function restoreRasterMonitor() {
    try {
      const raw = localStorage.getItem(RASTER_MONITOR_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      activeRasterJobId = String(parsed?.activeRasterJobId || "").trim();
      monitoredRasterJobId = String(parsed?.monitoredRasterJobId || "").trim();
      monitoredRasterJob = parsed?.monitoredRasterJob && typeof parsed.monitoredRasterJob === "object"
        ? parsed.monitoredRasterJob
        : null;
      if (activeRasterJobId || monitoredRasterJobId) {
        stopRasterJobPolling();
        pollRasterJob({ force: true });
        rasterJobPollTimer = setInterval(pollRasterJob, 1200);
      }
    } catch {}
  }

  return { mount, snapshot, restore, resolveReference, destroy };
}

function isGeoTiffFile(file) {
  const name = String(file?.name || "").toLowerCase();
  return name.endsWith(".tif") || name.endsWith(".tiff");
}

function normalizeBaseName(fileName) {
  const name = String(fileName || "").trim();
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.slice(0, dot) : name).toLowerCase();
}

function extensionOf(fileName) {
  const name = String(fileName || "").trim().toLowerCase();
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot) : "";
}

function buildSidecarLookup(files) {
  const worldByBase = new Map();
  const prjByBase = new Map();
  const metadataByBase = new Map();
  const worldExts = new Set([".tfw", ".tifw", ".tiffw", ".wld"]);
  files.forEach((file) => {
    const ext = extensionOf(file?.name);
    const base = sidecarBaseName(file?.name);
    if (!base) return;
    if (worldExts.has(ext) && !worldByBase.has(base)) worldByBase.set(base, file);
    if (ext === ".prj" && !prjByBase.has(base)) prjByBase.set(base, file);
    if ((/\.tif\.xml$/i.test(String(file?.name || "")) || /\.tiff\.xml$/i.test(String(file?.name || "")) || /\.aux\.xml$/i.test(String(file?.name || ""))) && !metadataByBase.has(base)) {
      metadataByBase.set(base, file);
    }
  });
  return { worldByBase, prjByBase, metadataByBase };
}

function sidecarBaseName(fileName) {
  const name = String(fileName || "").trim();
  if (!name) return "";
  const lower = name.toLowerCase();
  if (lower.endsWith(".tif.aux.xml")) return name.slice(0, -".tif.aux.xml".length).toLowerCase();
  if (lower.endsWith(".tiff.aux.xml")) return name.slice(0, -".tiff.aux.xml".length).toLowerCase();
  if (lower.endsWith(".tif.xml")) return name.slice(0, -".tif.xml".length).toLowerCase();
  if (lower.endsWith(".tiff.xml")) return name.slice(0, -".tiff.xml".length).toLowerCase();
  if (lower.endsWith(".aux.xml")) return name.slice(0, -".aux.xml".length).toLowerCase();
  return normalizeBaseName(name);
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("File read failed."));
    reader.readAsText(file);
  });
}

function parseWorldFileText(text) {
  const nums = String(text || "")
    .split(/[\r\n]+/)
    .map((line) => Number(String(line || "").trim()))
    .filter((n) => Number.isFinite(n));
  if (nums.length < 6) return null;
  return nums.slice(0, 6);
}

function inferProjectionCodeFromWkt(wkt) {
  const raw = String(wkt || "");
  if (!raw) return "";
  const matches = [...raw.matchAll(/AUTHORITY\s*\[\s*"EPSG"\s*,\s*"(\d+)"\s*\]/gi)];
  if (!matches.length) return "";
  const code = String(matches[matches.length - 1]?.[1] || "").trim();
  return code ? `EPSG:${code}` : "";
}

function decodeXmlEntities(value) {
  return String(value || "")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, "\"")
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

function inferProjectionFromMetadataXml(text) {
  const raw = String(text || "");
  if (!raw) return { projectionCode: "", projectionDef: "", geographicBounds: null };
  const wkidMatch = raw.match(/<WKID>\s*(\d+)\s*<\/WKID>/i) || raw.match(/<identCode[^>]*>\s*(\d+)\s*<\/identCode>/i);
  const projectionCode = wkidMatch?.[1] ? `EPSG:${String(wkidMatch[1]).trim()}` : "";
  const geographicBounds = inferGeographicBoundsFromMetadataXml(raw);
  const escapedWktMatch = raw.match(/&lt;WKT&gt;([\s\S]*?)&lt;\/WKT&gt;/i);
  if (escapedWktMatch?.[1]) {
    const projectionDef = decodeXmlEntities(escapedWktMatch[1]).trim();
    return { projectionCode: projectionCode || inferProjectionCodeFromWkt(projectionDef), projectionDef, geographicBounds };
  }
  const plainWktMatch = raw.match(/<WKT>\s*([\s\S]*?)\s*<\/WKT>/i);
  if (plainWktMatch?.[1]) {
    const projectionDef = decodeXmlEntities(plainWktMatch[1]).trim();
    return { projectionCode: projectionCode || inferProjectionCodeFromWkt(projectionDef), projectionDef, geographicBounds };
  }
  return { projectionCode, projectionDef: "", geographicBounds };
}

function inferGeographicBoundsFromMetadataXml(xmlText) {
  const readAll = (tag) => [...String(xmlText || "").matchAll(new RegExp(`<${tag}[^>]*>\\s*(-?\\d+(?:\\.\\d+)?)\\s*<\\/${tag}>`, "gi"))]
    .map((m) => Number(m?.[1]))
    .filter((n) => Number.isFinite(n));
  const westVals = readAll("westBL");
  const eastVals = readAll("eastBL");
  const southVals = readAll("southBL");
  const northVals = readAll("northBL");
  const count = Math.min(westVals.length, eastVals.length, southVals.length, northVals.length);
  for (let i = count - 1; i >= 0; i -= 1) {
    const west = westVals[i];
    const east = eastVals[i];
    const south = southVals[i];
    const north = northVals[i];
    if (!Number.isFinite(west) || !Number.isFinite(east) || !Number.isFinite(south) || !Number.isFinite(north)) continue;
    if (west >= east || south >= north) continue;
    if (west < -180 || east > 180 || south < -90 || north > 90) continue;
    return [west, east, south, north];
  }
  return null;
}

function debugNotify(app, message) {
  const text = String(message || "");
  console.warn(`[OverlayManager Debug] ${text}`);
  try { app?.ui?.toast?.(text); } catch {}
}
